import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Cloud, Sun, CloudRain, CloudSnow, CloudLightning, Wind, Droplets, ThermometerSun, MapPin, RefreshCw } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Weather icon mapping
const WEATHER_ICONS = {
  Clear: Sun,
  Clouds: Cloud,
  Rain: CloudRain,
  Drizzle: CloudRain,
  Thunderstorm: CloudLightning,
  Snow: CloudSnow,
  Mist: Wind,
  Fog: Wind,
  Haze: Wind,
  default: Cloud,
};

// Weather colors
const WEATHER_COLORS = {
  Clear: "text-yellow-400",
  Clouds: "text-slate-400",
  Rain: "text-blue-400",
  Drizzle: "text-blue-300",
  Thunderstorm: "text-purple-400",
  Snow: "text-cyan-200",
  Mist: "text-slate-300",
  default: "text-slate-400",
};

export const WeatherWidget = ({ onWeatherChange, compact = false }) => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationDenied, setLocationDenied] = useState(false);

  const fetchWeather = useCallback(async (lat, lon) => {
    try {
      const response = await axios.get(`${API}/weather`, {
        params: { lat, lon },
        timeout: 15000
      });
      setWeather(response.data);
      onWeatherChange?.(response.data.weather_code);
      setError(null);
    } catch (err) {
      console.error("Weather fetch error:", err);
      if (err.response?.status === 503) {
        setError("Servicio del clima no disponible");
      } else {
        setError("Error al obtener el clima");
      }
    } finally {
      setLoading(false);
    }
  }, [onWeatherChange]);

  const getLocation = useCallback(() => {
    setLoading(true);
    setError(null);
    
    if (!navigator.geolocation) {
      setError("Geolocalización no soportada");
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        fetchWeather(position.coords.latitude, position.coords.longitude);
        setLocationDenied(false);
      },
      (err) => {
        console.error("Geolocation error:", err);
        if (err.code === 1) {
          setLocationDenied(true);
          setError("Permiso de ubicación denegado");
        } else {
          setError("No se pudo obtener la ubicación");
        }
        setLoading(false);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }, [fetchWeather]);

  useEffect(() => {
    getLocation();
    // Refresh weather every 30 minutes
    const interval = setInterval(getLocation, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [getLocation]);

  const WeatherIcon = weather ? (WEATHER_ICONS[weather.weather_code] || WEATHER_ICONS.default) : Cloud;
  const iconColor = weather ? (WEATHER_COLORS[weather.weather_code] || WEATHER_COLORS.default) : "text-slate-400";

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10"
        data-testid="weather-widget-compact"
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-neon-cyan/30 border-t-neon-cyan rounded-full animate-spin" />
        ) : weather ? (
          <>
            <span className="text-lg">{weather.icon}</span>
            <span className="text-sm text-slate-300 font-rajdhani">
              {Math.round(weather.temp)}°C
            </span>
          </>
        ) : (
          <Cloud className="w-4 h-4 text-slate-500" />
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 rounded-xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10"
      data-testid="weather-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-rajdhani font-semibold text-slate-300 text-sm flex items-center gap-2">
          <ThermometerSun className="w-4 h-4 text-neon-cyan" />
          Clima Actual
        </h3>
        <button
          onClick={getLocation}
          disabled={loading}
          className="p-1 rounded-lg hover:bg-white/10 transition-colors"
          title="Actualizar clima"
          data-testid="refresh-weather-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <div className="w-8 h-8 border-3 border-neon-cyan/30 border-t-neon-cyan rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="text-center py-2">
          <p className="text-xs text-slate-500">{error}</p>
          {locationDenied && (
            <button
              onClick={getLocation}
              className="mt-2 text-xs text-neon-cyan hover:underline"
            >
              Permitir ubicación
            </button>
          )}
        </div>
      ) : weather && (
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 text-4xl">
            {weather.icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-orbitron text-white">
                {Math.round(weather.temp)}°
              </span>
              <span className="text-sm text-slate-400">C</span>
            </div>
            <p className="text-xs text-slate-400 capitalize truncate">
              {weather.description}
            </p>
            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
              <MapPin className="w-3 h-3" />
              <span className="truncate">{weather.location}</span>
            </div>
          </div>
          <div className="flex flex-col gap-1 text-xs text-slate-400">
            <div className="flex items-center gap-1">
              <Droplets className="w-3 h-3" />
              <span>{weather.humidity}%</span>
            </div>
            <div className="flex items-center gap-1">
              <ThermometerSun className="w-3 h-3" />
              <span>{Math.round(weather.feels_like)}°</span>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Hook for getting weather in mood logging
export const useWeather = () => {
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await axios.get(`${API}/weather`, {
            params: {
              lat: position.coords.latitude,
              lon: position.coords.longitude
            }
          });
          setWeather(response.data.weather_code);
        } catch (err) {
          console.error("Weather hook error:", err);
        }
      },
      () => {}, // Silently fail if no permission
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 600000 }
    );
  }, []);

  return weather;
};
